/**
 * src/lib/offline/persistStorage.js — ask the browser to make our storage durable.
 *
 * The offline-first feature lives in IndexedDB (the read-cache in `entities` +
 * the `outbox` of queued edits). By default that storage is EVICTABLE — on iOS,
 * fully closing Safari can wipe IndexedDB while the Service Worker Cache Storage
 * (app shell/chunks) survives, so the app LOADS but the data is GONE. That is the
 * 2026-06-06 "offline board empty / test task disappeared" report.
 *
 * `navigator.storage.persist()` asks the browser to exempt this origin from
 * eviction. Reality on iOS: it's granted reliably only to a Home-Screen-INSTALLED
 * (standalone) PWA; in a plain Safari tab it's typically DENIED. We request it
 * anyway (no-op if already granted, harmless if denied) and report the outcome so
 * we can see, per device, whether durable storage is actually in effect.
 */

/** True when running as an installed/standalone PWA (where iOS grants durable storage). */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.navigator && window.navigator.standalone === true) return true; // iOS Safari
    if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // matchMedia absent (e.g. jsdom) — fall through to non-standalone
  }
  return false;
}

/**
 * Best-effort request for persistent storage. Never throws.
 * @returns {Promise<{supported:boolean, persisted:boolean, already:boolean, standalone:boolean, quota?:number, usage?:number, error?:string}>}
 */
export async function requestPersistentStorage() {
  const standalone = isStandalone();
  if (typeof navigator === 'undefined' || !navigator.storage || typeof navigator.storage.persist !== 'function') {
    return { supported: false, persisted: false, already: false, standalone };
  }
  try {
    const already = typeof navigator.storage.persisted === 'function'
      ? await navigator.storage.persisted()
      : false;
    const persisted = already ? true : await navigator.storage.persist();
    let quota;
    let usage;
    if (typeof navigator.storage.estimate === 'function') {
      try {
        const e = await navigator.storage.estimate();
        quota = e?.quota;
        usage = e?.usage;
      } catch {
        // estimate is optional telemetry — ignore if it fails
      }
    }
    return { supported: true, persisted: !!persisted, already: !!already, standalone, quota, usage };
  } catch (e) {
    return { supported: true, persisted: false, already: false, standalone, error: e?.message || String(e) };
  }
}
